import React from 'react';
import { brandIdentityItems, bottleDesignPoints, IMAGES, BOTTLES } from '../mock';
import { BadgeCheck, FileBadge, Copyright, ShieldAlert, Check } from 'lucide-react';
import { Reveal, StaggerGroup, StaggerItem } from './Reveal';
import { motion } from 'framer-motion';

const iconMap = { BadgeCheck, FileBadge, Copyright, ShieldAlert };

const About = () => {
  return (
    <section id="about" className="relative py-24 lg:py-32 bg-[#fafaf7] overflow-hidden">
      {/* Section label */}
      <Reveal>
        <div className="max-w-[1440px] mx-auto px-6 lg:px-16 mb-16">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-10 h-[1px] bg-[#c4a24a]" />
            <span className="text-[10px] font-medium tracking-[0.35em] text-[#c4a24a] uppercase">
              02 — The Brand
            </span>
          </div>
          <div className="grid lg:grid-cols-12 gap-6 items-end">
            <h2
              className="lg:col-span-8 text-[42px] lg:text-[64px] xl:text-[72px] leading-[1.03] font-medium text-[#0f2c6b] text-balance"
              style={{
                fontFamily: 'Fraunces, Playfair Display, serif',
                fontVariationSettings: '"opsz" 144',
                letterSpacing: '-0.03em',
              }}
            >
              Hydration <span className="italic text-[#c4a24a] font-light">beyond</span> expectations.
            </h2>
            <p className="lg:col-span-4 text-[14.5px] leading-[1.8] text-slate-600 font-light">
              Tropic Blue® is a premium packaged drinking water brand committed to delivering purity, safety, and excellence in every bottle.
            </p>
          </div>
        </div>
      </Reveal>

      <div className="max-w-[1440px] mx-auto px-6 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Column 1: Brand story */}
          <Reveal delay={0.1} className="lg:col-span-3 flex flex-col justify-between">
            <div className="space-y-5 text-[14px] leading-[1.85] text-slate-600 font-light">
              <p>
                Manufactured using advanced multi-stage purification including <span className="text-[#0f2c6b] font-medium">RO, UV and Ozonation</span>, our water meets stringent quality standards to ensure consistent taste and safety.
              </p>
              <p>
                Designed for consumers who value quality, hygiene, and premium presentation, Tropic Blue has become a trusted hydration partner for hospitality, retail, corporate and institutional sectors.
              </p>
              <div className="pt-4 border-t border-slate-200">
                <div className="text-[36px] font-medium text-[#0f2c6b]" style={{ fontFamily: 'Fraunces, serif' }}>
                  10<span className="text-[#c4a24a]">+</span>
                </div>
                <div className="text-[10.5px] tracking-[0.28em] text-slate-500 uppercase mt-1">
                  Stage Purification
                </div>
              </div>
            </div>
          </Reveal>

          {/* Column 2: Bottle showcase with matching cream aesthetic */}
          <Reveal delay={0.2} className="lg:col-span-3 relative min-h-[520px] overflow-hidden group">
            {/* Cream gradient bg matching bottle stage */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  'radial-gradient(ellipse at 50% 45%, #f5efdf 0%, #eae0c5 55%, #c9bc98 100%)',
              }}
            />
            {/* Warm light beam */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  'radial-gradient(ellipse at 50% 15%, rgba(255,240,200,0.45) 0%, transparent 55%)',
              }}
            />
            {/* Subtle grain */}
            <div
              className="absolute inset-0 opacity-25 mix-blend-multiply"
              style={{
                background:
                  "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3CfeColorMatrix values='0 0 0 0 0.4 0 0 0 0 0.3 0 0 0 0 0.15 0 0 0 0.2 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
              }}
            />

            <div className="absolute inset-0 flex items-center justify-center">
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 1.2, delay: 0.4 }}
                viewport={{ once: true }}
                className="animate-float"
              >
                <img
                  src={BOTTLES.boat}
                  alt="Tropic Blue Boat"
                  className="h-[400px] lg:h-[460px] max-w-[85%] w-auto object-contain drop-shadow-[0_30px_50px_rgba(15,44,107,0.35)]"
                />
              </motion.div>
            </div>
            {/* Ground reflection */}
            <div className="absolute bottom-16 left-1/2 -translate-x-1/2 w-40 h-4 bg-black/20 rounded-full blur-md" />

            <div className="absolute bottom-5 left-5 right-5 text-[#0f2c6b]">
              <div className="text-[10px] tracking-[0.35em] mb-1 text-[#0f2c6b]/70 uppercase font-medium">
                Sourced with care
              </div>
              <div className="text-[13px] font-medium" style={{ fontFamily: 'Fraunces, serif' }}>
                Bottle trademarks managed by Savaro India.
              </div>
            </div>
          </Reveal>

          {/* Column 3: Brand Identity */}
          <div className="lg:col-span-3 flex flex-col">
            <Reveal delay={0.2}>
              <div className="mb-6">
                <div className="text-[10px] tracking-[0.35em] text-[#c4a24a] mb-3 uppercase font-medium">
                  IP &amp; Brand Protection
                </div>
                <h3
                  className="text-[26px] lg:text-[32px] leading-[1.15] font-medium text-[#0f2c6b]"
                  style={{ fontFamily: 'Fraunces, serif', letterSpacing: '-0.02em' }}
                >
                  Our brand. <span className="italic font-light">Our identity.</span>
                </h3>
              </div>
            </Reveal>
            <StaggerGroup className="space-y-5">
              {brandIdentityItems.map((item, idx) => {
                const Icon = iconMap[item.icon];
                return (
                  <StaggerItem key={idx}>
                    <div className="group flex gap-3 pb-4 border-b border-slate-200 last:border-0">
                      <div className="shrink-0 w-10 h-10 rounded-full bg-white border border-[#c4a24a]/30 flex items-center justify-center text-[#0f2c6b] group-hover:border-[#c4a24a] group-hover:bg-[#0f2c6b] group-hover:text-white transition-all duration-500">
                        <Icon size={16} strokeWidth={1.5} />
                      </div>
                      <div>
                        <h4 className="text-[11.5px] font-semibold text-[#0f2c6b] tracking-[0.15em] mb-1.5">
                          {item.title}
                        </h4>
                        <p className="text-[13px] leading-[1.6] text-slate-600 font-light">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </StaggerItem>
                );
              })}
            </StaggerGroup>
          </div>

          {/* Column 4: Registered Bottle Design (Premium Dark Panel) */}
          <Reveal delay={0.3} x={30} className="lg:col-span-3">
            <div className="relative bg-gradient-to-br from-[#0a1f4a] via-[#0f2c6b] to-[#0a1f4a] text-white p-8 lg:p-9 rounded-none overflow-hidden h-full">
              {/* Decorative pattern */}
              <div className="absolute -top-20 -right-20 w-56 h-56 bg-white/[0.03] rounded-full" />
              <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-[#c4a24a]/[0.05] rounded-full" />

              <div className="relative">
                <div className="text-[10px] tracking-[0.35em] text-[#c4a24a] mb-6 uppercase font-medium">
                  Exclusive Registered<br />Bottle Design
                </div>
                <p className="text-[13.5px] text-white/70 mb-6 leading-[1.75] font-light">
                  Our distinctive bottle silhouette has been developed to create:
                </p>
                <ul className="space-y-3 pb-28">
                  {bottleDesignPoints.map((point, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-[13.5px] text-white/90 font-light">
                      <Check size={14} className="text-[#c4a24a] mt-1 shrink-0" strokeWidth={2.5} />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>

                {/* Premium badge */}
                <motion.div
                  animate={{ rotate: [0, 5, 0, -5, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                  className="absolute -bottom-2 -right-2 w-24 h-24 rounded-full border border-[#c4a24a]/60 flex flex-col items-center justify-center bg-[#0a1f4a] text-[#c4a24a]"
                >
                  <div className="text-[8px] tracking-[0.25em] font-medium">REGISTERED</div>
                  <svg className="w-5 h-5 my-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <div className="text-[8px] tracking-[0.25em] font-medium">DESIGN</div>
                </motion.div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default About;
